import type { QueueMember, RoutingSkill, Team, TeamLine, TeamPerson } from "@/lib/types";

/**
 * Teams (platform ADR-185): a group of people with its leads (ADR-207), and the campaign lines it
 * staffs.
 * Everyone in a team who has a phone is on each of its lines. Skills stay on the rep: a team gives
 * nobody one, which is why the team page says who will not be offered a line's calls.
 */

/** A skill one of the team's lines asks for that a member does not hold, at the level asked. */
export interface MissingSkill {
  slug: string;
  name: string;
  minLevel: number;
  /** The lines that ask for it, by name. */
  lines: string[];
}

/** What keeps a member from being offered the calls of the team's lines, one entry per skill.
 *  A member with no phone is on no line, so nothing is missing for them. */
export function missingSkills(
  person: TeamPerson,
  lines: readonly TeamLine[],
  catalog: readonly RoutingSkill[] = [],
): MissingSkill[] {
  if (person.extension === null) return [];
  const bySlug = new Map<string, MissingSkill>();
  for (const line of lines) {
    for (const req of line.required_skills) {
      const level = person.skills[req.slug] ?? 0;
      const min = req.min_level ?? 1;
      if (level >= min) continue;
      const known = bySlug.get(req.slug);
      if (known) {
        known.minLevel = Math.max(known.minLevel, min);
        if (!known.lines.includes(line.queue_name)) known.lines.push(line.queue_name);
      } else {
        const name = catalog.find((s) => s.slug === req.slug)?.name ?? req.slug;
        bySlug.set(req.slug, { slug: req.slug, name, minLevel: min, lines: [line.queue_name] });
      }
    }
  }
  return [...bySlug.values()];
}

/** How a missing skill reads beside the member: "No Closer", or "Closer below 3" for a level. */
export function missingText(missing: MissingSkill, held: number | undefined): string {
  return held === undefined || held === 0
    ? `No ${missing.name}`
    : `${missing.name} ${held}, the line asks for ${missing.minLevel}`;
}

/** The teams on a campaign's lines, one row per team and line, primary line first. */
export interface TeamOnLine {
  team: Team;
  line: TeamLine;
}

const PURPOSE_ORDER = ["primary", "closer", "inbound", "custom"];

export function teamsOnCampaign(teams: readonly Team[], campaignId: string): TeamOnLine[] {
  const out: TeamOnLine[] = [];
  for (const team of teams) {
    for (const line of team.lines) {
      if (line.campaign_id === campaignId) out.push({ team, line });
    }
  }
  const rank = (p: string | null) => {
    const i = PURPOSE_ORDER.indexOf(p ?? "custom");
    return i === -1 ? PURPOSE_ORDER.length : i;
  };
  return out.sort(
    (a, b) =>
      rank(a.line.purpose) - rank(b.line.purpose) ||
      a.line.queue_name.localeCompare(b.line.queue_name) ||
      a.team.name.localeCompare(b.team.name),
  );
}

/** The members a team puts on a line: the ones with a phone. */
export function onLineCount(team: Team): number {
  return team.members.filter((m) => m.extension !== null).length;
}

/** The reps somebody added to the campaign's lines by hand. A core-api older than teams does not
 *  say, and there every rep was added by hand. */
export function addedByHand(members: readonly QueueMember[]): QueueMember[] {
  return members.filter((m) => m.by_hand !== false);
}

/** How many people take the campaign's calls, each counted once however many lines and teams
 *  put them there, and how many of them only a team does. */
export function staffingCounts(members: readonly QueueMember[]): { people: number; byTeamOnly: number } {
  const byPhone = new Map<string, boolean>();
  for (const m of members) {
    const key = `${m.user}@${m.domain}`;
    byPhone.set(key, (byPhone.get(key) ?? false) || m.by_hand !== false);
  }
  let byTeamOnly = 0;
  for (const byHand of byPhone.values()) if (!byHand) byTeamOnly += 1;
  return { people: byPhone.size, byTeamOnly };
}

/** "Maria Lopez", or the email of somebody with no name. */
export function personName(p: Pick<TeamPerson, "display_name" | "email">): string {
  return p.display_name?.trim() || p.email;
}

/** The team's page under its name: "Led by Maria and Ali. 4 people. Anyone can chat with this
 *  team." */
export function teamSummary(team: Pick<Team, "leads" | "members" | "open_chat">): string {
  const led = leadNames(team);
  const people = `${team.members.length} ${team.members.length === 1 ? "person" : "people"}.`;
  return `${led ? `Led by ${led}.` : "No lead yet."} ${people}${team.open_chat ? " Anyone can chat with this team." : ""}`;
}

/** The team's leads by name, "Maria and Ali", "Maria, Ali and Sam"; null for none. */
export function leadNames(team: Pick<Team, "leads">): string | null {
  const names = (team.leads ?? []).map(personName);
  const last = names.pop();
  if (last === undefined) return null;
  return names.length ? `${names.join(", ")} and ${last}` : last;
}
